// src/components/ImageEditor/ImageCropModal.jsx
//
// Crop / reposition / rotate an image that is ALREADY uploaded, then replace the
// stored reference with the edited version. Post-upload by design: a photo taken
// on a phone via the QR flow is uploaded by the phone, so the desktop can only
// ever edit it after the fact — and running one flow for both keeps behaviour
// identical whichever way the image arrived.
//
// The pre-edit file is left on ImageKit rather than deleted, so the original
// photo is never destroyed by an edit.

import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Modal, Button, Slider, Space, Typography, Segmented, message, Tooltip,
} from 'antd';
import {
  RotateLeftOutlined, RotateRightOutlined, ZoomInOutlined,
  ExpandOutlined, UndoOutlined,
} from '@ant-design/icons';
import cropImage from './cropImage';
import { uploadEditedImage } from './uploadEditedImage';

const { Text } = Typography;

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

const ASPECTS = [
  { label: 'Free', value: 'free' },
  { label: '1:1', value: '1' },
  { label: '4:3', value: '1.3333' },
  { label: '3:4', value: '0.75' },
  { label: '16:9', value: '1.7778' },
];

/**
 * @param {boolean}  open
 * @param {Object}   image        the file ref being edited ({ url, name, ... })
 * @param {string}   folderPath   ImageKit folder for the edited copy
 * @param {Function} onCancel
 * @param {Function} onCropped    (newFileRef) => void — the uploaded replacement
 * @param {number}   [defaultAspect]
 */
const ImageCropModal = ({ open, image, folderPath, onCancel, onCropped, defaultAspect }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(defaultAspect ? String(defaultAspect) : 'free');
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const src = image?.url || image?.src || null;

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const onCropComplete = useCallback((_, areaPixels) => setCroppedAreaPixels(areaPixels), []);

  const handleCropChange = useCallback((next) => {
    setCrop((prev) => (prev.x === next.x && prev.y === next.y ? prev : next));
  }, []);

  const handleZoomChange = useCallback((next) => {
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    setZoom((prev) => (Math.abs(prev - clamped) < 1e-4 ? prev : clamped));
  }, []);

  const handleRotationChange = useCallback((next) => {
    setRotation((prev) => (Math.abs(prev - next) < 1e-4 ? prev : next));
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels || !src) return;
    setSaving(true);
    try {
      const blob = await cropImage(src, croppedAreaPixels, rotation);
      const baseName = (image?.name || 'image').replace(/\.[^.]+$/, '');
      const fileRef = await uploadEditedImage(blob, {
        fileName: `${baseName}-edited.jpg`,
        folderPath,
      });
      messageApi.success('Image updated');
      onCropped?.(fileRef);
      reset();
    } catch (err) {
      console.error('Crop/save failed:', err);
      messageApi.error(
        err?.response?.data?.message || err?.message || 'Could not save the edited image',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Crop & Reposition"
        open={open}
        onCancel={handleCancel}
        width={640}
        destroyOnClose
        footer={[
          <Button key="reset" icon={<UndoOutlined />} onClick={reset} disabled={saving}>
            Reset
          </Button>,
          <Button key="cancel" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            disabled={!croppedAreaPixels}
            onClick={handleSave}
          >
            Save
          </Button>,
        ]}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 360,
            background: '#333',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect === 'free' ? undefined : Number(aspect)}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              onCropChange={handleCropChange}
              onZoomChange={handleZoomChange}
              onRotationChange={handleRotationChange}
              onCropComplete={onCropComplete}
              showGrid
            />
          )}
        </div>

        <Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '10px 0 4px' }}>
          Drag the image to reposition it. Pinch or use the slider to zoom.
        </Text>

        <div style={{ marginTop: 8 }}>
          <Space align="center" style={{ width: '100%' }}>
            <Tooltip title="Zoom"><ZoomInOutlined /></Tooltip>
            <Slider
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={handleZoomChange}
              style={{ width: 200 }}
              tooltip={{ formatter: (v) => `${v.toFixed(2)}x` }}
            />
            <Tooltip title="Rotate left 90°">
              <Button icon={<RotateLeftOutlined />} onClick={() => setRotation((r) => r - 90)} />
            </Tooltip>
            <Tooltip title="Rotate right 90°">
              <Button icon={<RotateRightOutlined />} onClick={() => setRotation((r) => r + 90)} />
            </Tooltip>
          </Space>
        </div>

        <div style={{ marginTop: 12 }}>
          <Space align="center" wrap>
            <ExpandOutlined />
            <Text style={{ fontSize: 12 }}>Aspect</Text>
            <Segmented
              size="small"
              options={ASPECTS}
              value={aspect}
              onChange={setAspect}
            />
          </Space>
        </div>

        <div style={{ marginTop: 10 }}>
          <Space align="center">
            <Text style={{ fontSize: 12 }}>Fine rotation</Text>
            <Slider
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={handleRotationChange}
              style={{ width: 240 }}
              tooltip={{ formatter: (v) => `${v}°` }}
            />
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default ImageCropModal;
